import axios from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { AdminQueryRoot } from '@/lib/organization-scoping'
import type {
  OrganizationScope,
  CashDrawerSettings,
  CurrentCashDrawer,
  CashDrawerStatus,
  OpenCashDrawers,
  CashDrawerLocation,
  CashDrawerWorkstation,
  CashDrawerSession,
  CashDrawerSessionNavigation,
  UpdateCashDrawerSettingsInput,
  CreateCashDrawerWorkstationInput,
  UpdateCashDrawerWorkstationInput,
  StartCashDrawerInput,
  AdjustCashDrawerInput,
  CloseCashDrawerInput,
  CashDrawerSessionInput,
} from './cash-drawers.types'

type CashDrawerQueryContext = Pick<
  QueryFunctionContext<[Omit<AdminQueryRoot, 'organizationId'> & OrganizationScope]>,
  'queryKey'
>

export async function getCashDrawerSettings({
  queryKey: [{ organizationId }],
}: CashDrawerQueryContext) {
  return axios.get<CashDrawerSettings>(
    '/settings/cash-drawer',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateCashDrawerSettings({
  organizationId,
  enabled,
  expected_version,
}: OrganizationScope & UpdateCashDrawerSettingsInput) {
  return axios.put<CashDrawerSettings>(
    '/settings/cash-drawer',
    { enabled, expected_version },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCurrentCashDrawer({
  queryKey: [{ organizationId, workstationId }],
}: CashDrawerQueryContext) {
  return axios.get<CurrentCashDrawer>(
    '/cash-drawer-sessions/current',
    buildAdminRequestConfig({
      organizationId,
      params: { cash_drawer_workstation_id: workstationId },
    })
  )
}

export async function getCashDrawerStatus({
  queryKey: [{ organizationId }],
}: CashDrawerQueryContext) {
  return axios.get<CashDrawerStatus>(
    '/cash-drawer-sessions/status',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getOpenCashDrawers({
  queryKey: [{ organizationId }],
}: CashDrawerQueryContext) {
  return axios.get<OpenCashDrawers>(
    '/cash-drawer-sessions/open',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCashDrawerLocations({
  queryKey: [{ organizationId }],
}: CashDrawerQueryContext) {
  return axios.get<CashDrawerLocation[]>(
    '/cash-drawer-locations',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getCashDrawerWorkstations({
  queryKey: [{ organizationId }],
}: CashDrawerQueryContext) {
  return axios.get<CashDrawerWorkstation[]>(
    '/cash-drawer-workstations',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCashDrawerWorkstation({
  organizationId,
  ...values
}: OrganizationScope & CreateCashDrawerWorkstationInput) {
  return axios.post<CashDrawerWorkstation>(
    '/cash-drawer-workstations',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateCashDrawerWorkstation({
  organizationId,
  workstationId,
  ...values
}: OrganizationScope & UpdateCashDrawerWorkstationInput) {
  return axios.put<CashDrawerWorkstation>(
    `/cash-drawer-workstations/${workstationId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function startCashDrawer({
  organizationId,
  ...values
}: OrganizationScope & StartCashDrawerInput) {
  return axios.post<CashDrawerSession>(
    '/cash-drawer-sessions',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function adjustCashDrawer({
  organizationId,
  sessionId,
  ...values
}: OrganizationScope & AdjustCashDrawerInput) {
  return axios.post<CashDrawerSession>(
    `/cash-drawer-sessions/${sessionId}/adjustments`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function closeCashDrawer({
  organizationId,
  sessionId,
  ...values
}: OrganizationScope & CloseCashDrawerInput) {
  return axios.post<CashDrawerSession>(
    `/cash-drawer-sessions/${sessionId}/close`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function printCashDrawerSlip({
  organizationId,
  sessionId,
}: OrganizationScope & CashDrawerSessionInput) {
  return axios.get<string>(
    `/cash-drawer-sessions/${sessionId}/slip`,
    buildAdminRequestConfig({ organizationId, responseType: 'text' })
  )
}

export async function getCashDrawerSessionNavigation({
  queryKey: [{ organizationId, workstationId, sessionId }],
}: CashDrawerQueryContext) {
  return axios.get<CashDrawerSessionNavigation>(
    '/reports/cash-drawer/sessions',
    buildAdminRequestConfig({
      organizationId,
      params: { workstation_id: workstationId, session_id: sessionId },
    })
  )
}
